/**
 * Mobile bottom tab bar — pixel-faithful port of `TabBar` in
 * `design/project/shared.jsx`:
 *
 * 3-cell layout: 帳本 (home) / FAB-spacer / 分析 (analytics).
 *
 * Settings is reached via the home avatar's `aria-label="設定"` button
 * (top-right of HomeScreen). 紀錄 (AllTx) is reached via the「全部 →」link
 * inside the 近期紀錄 section. 通知 / 搜尋 use the bell / 放大鏡 icons in
 * the home top bar. 固定 lives inside Settings sub-pages.
 */
import Icon, { type IconName } from '@/components/common/Icon';
import { haptic } from '@/lib/haptics';

export type MobileTabKey = 'home' | 'analytics';

interface TabItem {
  k: MobileTabKey;
  label: string;
  icon: IconName;
}

const TABS: ReadonlyArray<TabItem> = [
  { k: 'home', label: '帳本', icon: 'home' },
  { k: 'analytics', label: '分析', icon: 'chart' },
];

export interface TabBarProps {
  /** null when no tab matches the current route (e.g. settings deep page). */
  active: MobileTabKey | null;
  onChange: (k: MobileTabKey) => void;
}

export default function TabBar({ active, onChange }: TabBarProps): JSX.Element {
  return (
    <nav
      aria-label="主導覽"
      data-testid="tab-bar"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 90,
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
        background: 'rgba(251,247,241,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '0.5px solid var(--border-1)',
        display: 'flex',
        zIndex: 20,
      }}
    >
      <TabButton
        item={TABS[0]!}
        active={active === 'home'}
        onClick={() => {
          haptic('tap');
          onChange('home');
        }}
      />
      {/* Center spacer — FAB hovers above this column. */}
      <div aria-hidden="true" style={{ flex: 1 }} />
      <TabButton
        item={TABS[1]!}
        active={active === 'analytics'}
        onClick={() => {
          haptic('tap');
          onChange('analytics');
        }}
      />
    </nav>
  );
}

function TabButton({
  item,
  active,
  onClick,
}: {
  item: TabItem;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: 1,
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        padding: 0,
        color: active ? 'var(--fg-1)' : 'var(--fg-3)',
      }}
    >
      <Icon name={item.icon} size={24} stroke="currentColor" />
      <div
        style={{
          font: `${active ? 600 : 500} 10px var(--font-cjk)`,
          letterSpacing: '0.04em',
        }}
      >
        {item.label}
      </div>
    </button>
  );
}
